import PropTypes from "prop-types";
import { useState } from "react";
import { Box, Button, Stack, TextField, Typography } from "@mui/material";

// Validation criteria
const MAX_NAME_LENGTH = 50;
const INVALID_CHARS = /[/\\<>:"|?*]/g;

// Validation
const validateName = (name) => {
  const nameTrimmed = name.trim();
  if (!nameTrimmed) return "Name is required.";
  if (nameTrimmed.length > MAX_NAME_LENGTH) {
    return `Name must not exceed ${MAX_NAME_LENGTH} characters.`;
  }
  const invalidChars = [...new Set(nameTrimmed.match(INVALID_CHARS))];
  if (invalidChars.length > 0) {
    return 'Name should not include special characters.';
  }

  // Valid input -- show no error message.
  return "";
};

export const WelcomeScreen = ({ userName = "", onReady }) => {
  // Form value -- the updated name
  const [name, setName] = useState(userName);
  const nameTrimmed = name.trim();

  // Name validation
  const errorMessage = validateName(name);

  // Submission handling
  const handleSubmit = (event) => {
    event.preventDefault();
    if (!errorMessage) onReady(nameTrimmed);
  };

  // The component
  return (
    <Stack
      component="form"
      onSubmit={handleSubmit}
      noValidate
      alignItems="center"
      justifyContent="center"
      sx={{ minHeight: "100dvh", px: 2, py: 4 }}
    >
      <BannerImage />
      <PromptText />
      <NameTextField
        name={name}
        errorMessage={errorMessage}
        onNameSet={setName}
      />
      <SubmitButton disabled={Boolean(errorMessage)} />
    </Stack>
  );
};
WelcomeScreen.propTypes = {
  userName: PropTypes.string,
  onReady: PropTypes.func.isRequired,
};

const BannerImage = () => {
  return (
    <Box
      component="img"
      src={`${process.env.PUBLIC_URL}/banner.png`}
      alt="SnugFit web scanner welcome banner"
      sx={{ width: "100%" }}
    />
  );
};

const PromptText = () => {
  return (
    <Stack alignItems="center" justifyContent="center">
      <Typography
        variant="h4"
        component="h1"
        color="primary"
        sx={{ fontWeight: 700, mt: 8 }}
      >
        Hello!
      </Typography>
      <Typography align="center" sx={{ mt: 4 }}>
        Please update your name below to continue.
      </Typography>
    </Stack>
  );
};

const NameTextField = ({ name, errorMessage, onNameSet }) => {
  return (
    <TextField
      fullWidth
      value={name}
      onChange={(event) => onNameSet(event.target.value)}
      autoComplete="name"
      error={Boolean(errorMessage)}
      helperText={errorMessage || `${name.trim().length} / ${MAX_NAME_LENGTH}`}
      inputProps={{
        "aria-label": "Your name",
        enterKeyHint: "go",
        maxLength: MAX_NAME_LENGTH,
      }}
      sx={{
        mt: 1.5,
        "& .MuiOutlinedInput-root": {
          borderRadius: 3,
          "& fieldset": { borderColor: "primary.main", borderWidth: 2 },
        },
        "& .MuiFormHelperText-root": { textAlign: "right", mx: 0.5 },
      }}
    />
  );
};
NameTextField.propTypes = {
  name: PropTypes.string.isRequired,
  errorMessage: PropTypes.string.isRequired,
  onNameSet: PropTypes.func.isRequired,
};

const SubmitButton = ({ disabled }) => {
  return (
    <Button
      type="submit"
      variant="contained"
      disabled={disabled}
      sx={{
        mt: 8,
        width: "72%",
        py: 1.75,
        borderRadius: 999,
        textTransform: "none",
        fontSize: "1.15rem",
        fontWeight: 700,
        boxShadow: "none",
      }}
    >
      I'm ready
    </Button>
  );
};
SubmitButton.propTypes = {
  disabled: PropTypes.bool.isRequired,
};
